import type {
  IntakeDocumentResponse,
  PatientDocumentResponse,
} from '../../services/api/types.gen';

/**
 * How long the Documents scope waits for the episode context before giving up.
 * The API client sets no request timeout, so without this a request that hangs
 * rather than fails would hide every task-attached document indefinitely —
 * worse than showing an unscoped list, which is what expiring falls through to.
 */
export const SCOPE_HOLD_TIMEOUT_MS = 10_000;

export interface DocumentEpisodeScope {
  /** The episode on screen; null when the patient has none. */
  episodeId: string | null;
  /**
   * task id -> episode id over every one of the patient's live tasks. Empty when
   * the loaded tasks do not describe this patient, which makes every document
   * fall open rather than be attributed to the wrong episode.
   */
  taskEpisodeIds: ReadonlyMap<string, string>;
  /**
   * task id -> the date that task displays, over the same tasks. Separate from
   * the document's own `task_scheduled_date`, which is the scheduled date and so
   * disagrees with a visit charted on a different day.
   */
  taskDates: ReadonlyMap<string, string>;
  /**
   * False when the map could not be built (the task fetch failed), as opposed to
   * built and empty. The filter fails open either way, so this is what stops the
   * tab claiming an episode-filtered list it is not actually showing.
   *
   * Note for producers: false means the tasks are not known at all, so the
   * caller has no episode task list to offer either — the tab's copy assumes
   * `documentContextTasks` is empty whenever this is false.
   */
  taskEpisodeIdsKnown: boolean;
  /**
   * False until both the episode and the task fetch have settled for this
   * patient, either way. The tab shows nothing while it is false, so it must
   * become true even when a fetch fails.
   */
  ready: boolean;
  /**
   * True once the context stayed unready for SCOPE_HOLD_TIMEOUT_MS for this
   * patient. Stays true for the patient, so the tab keeps its All Episodes
   * fallback and the badge keeps counting it, even after a late arrival.
   */
  holdExpired: boolean;
}

/**
 * Intake-type patient documents are copies of intake documents, which the tab
 * lists from the intake itself, so the patient-document list leaves them out.
 */
export const isListedPatientDocument = (
  doc: Pick<PatientDocumentResponse, 'document_type'>
): boolean => doc.document_type !== 'intake';

/**
 * The intake documents the tab lists beside `shownPatientDocuments`. A tagged
 * intake file whose patient copy was relinked to an order on push-to-roger
 * keeps the intake file's `file_path` and becomes `document_type: 'order'`, so
 * when that order row is shown the intake row for the same file is dropped.
 * Matching only the rows shown keeps the file listed once in every view: as
 * the order in its own episode, as the intake row everywhere else.
 */
export const listedIntakeDocuments = <
  T extends Pick<IntakeDocumentResponse, 'file_path'>,
>(
  intakeDocuments: readonly T[],
  shownPatientDocuments: readonly Pick<
    PatientDocumentResponse,
    'document_type' | 'file_path'
  >[]
): T[] => {
  const orderFilePaths = new Set(
    shownPatientDocuments
      .filter(doc => doc.document_type === 'order')
      .map(doc => doc.file_path)
  );
  return intakeDocuments.filter(doc => !orderFilePaths.has(doc.file_path));
};

/**
 * True when the document's task is known to sit outside the episode on screen.
 * A task the map has never heard of is NOT treated as another episode's: the
 * map only covers live, episode-attached tasks that have finished loading, so
 * "unknown" means "cannot say". Since "This Episode" is the default view,
 * guessing "other episode" there would make a live clinical document
 * unreachable unless the user knew to switch scopes.
 */
export const isOutsideEpisode = (
  doc: Pick<PatientDocumentResponse, 'task_id'>,
  episodeId: string | null,
  taskEpisodeIds: ReadonlyMap<string, string>
): boolean => {
  if (!doc.task_id || episodeId == null) return false;
  const docEpisodeId = taskEpisodeIds.get(doc.task_id);
  return docEpisodeId !== undefined && docEpisodeId !== episodeId;
};

/**
 * Whether "This Episode" can be honoured at all. It needs an episode AND a
 * usable task map: with either missing, the predicate matches nothing and the
 * scope would sit selected over a completely unfiltered list, which reads as a
 * filtered one. Better to fall back to All Episodes and say why.
 */
export const isEpisodeScopeAvailable = (
  scope: Pick<DocumentEpisodeScope, 'episodeId' | 'taskEpisodeIdsKnown'>
): boolean => scope.episodeId != null && scope.taskEpisodeIdsKnown;

/**
 * How many rows the Documents tab lists before the user picks a scope: "This
 * Episode" when it can be honoured, otherwise every document, and every
 * document once the hold has expired. Intake documents are in every
 * episode-wide scope. Null while the episode context settles, so a badge never
 * shows a number it would then retract.
 */
export const countDefaultScopeDocuments = (
  patientDocuments: readonly Pick<
    PatientDocumentResponse,
    'document_type' | 'task_id' | 'file_path'
  >[],
  intakeDocuments: readonly Pick<IntakeDocumentResponse, 'file_path'>[],
  scope: Pick<
    DocumentEpisodeScope,
    | 'episodeId'
    | 'taskEpisodeIds'
    | 'taskEpisodeIdsKnown'
    | 'ready'
    | 'holdExpired'
  >
): number | null => {
  if (!scope.ready && !scope.holdExpired) return null;
  const listed = patientDocuments.filter(isListedPatientDocument);
  const inScope =
    !scope.holdExpired && isEpisodeScopeAvailable(scope)
      ? listed.filter(
          doc => !isOutsideEpisode(doc, scope.episodeId, scope.taskEpisodeIds)
        )
      : listed;
  return (
    inScope.length + listedIntakeDocuments(intakeDocuments, inScope).length
  );
};
